import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { UpgradeLock } from "@/components/UpgradeLock";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, Trash2, TrendingUp, AlertTriangle, Trophy, ListChecks, Target, Radar } from "lucide-react";

const L = {
  en: { title: "Weekly AI Report", subtitle: "Every Sunday, GPT 5.6 Terra reads your daily logs, injuries, stats, video scores and coach chats to spot patterns before they hurt performance.",
    generate: "Generate this week's report", regen: "Regenerate", generating: "Analyzing your week…", history: "Past reports", current: "This week",
    patterns: "Patterns", risks: "Risks", wins: "Wins", recos: "Recommendations", focus: "Next week focus",
    readiness: "Readiness", mental: "Mental", physical: "Physical", injury_risk: "Injury risk", based: "Based on", logs: "logs", empty: "No report yet. Log a few days then generate your first report." },
  fr: { title: "Rapport IA Hebdo", subtitle: "Chaque dimanche, GPT 5.6 Terra lit ton suivi quotidien, tes blessures, stats, scores vidéo et échanges coach pour repérer les tendances avant qu'elles ne plombent ta perf.",
    generate: "Générer mon rapport de la semaine", regen: "Régénérer", generating: "Analyse de ta semaine…", history: "Rapports précédents", current: "Cette semaine",
    patterns: "Tendances", risks: "Risques", wins: "Victoires", recos: "Recommandations", focus: "Focus semaine prochaine",
    readiness: "Fraîcheur", mental: "Mental", physical: "Physique", injury_risk: "Risque blessure", based: "Basé sur", logs: "entrées", empty: "Aucun rapport. Enregistre quelques jours puis génère ton premier rapport." },
};

const SCORE_META = [
  { k: "readiness", color: "#00F076" }, { k: "mental", color: "#60a5fa" }, { k: "physical", color: "#FFB800" }, { k: "injury_risk", color: "#FF2E54" },
];

const Section = ({ icon: Icon, title, items, color, testid }) => items?.length > 0 && (
  <div data-testid={testid}>
    <div className="text-xs font-mono-tech uppercase tracking-wider mb-1.5 flex items-center gap-1" style={{ color }}><Icon className="w-3.5 h-3.5" />{title}</div>
    <ul className="space-y-1 text-sm text-slate-300">{items.map((s, i) => <li key={i} className="pl-3 border-l-2" style={{ borderColor: `${color}55` }}>{s}</li>)}</ul>
  </div>
);

export const ReportCard = ({ doc, t, onDelete, isCurrent }) => {
  const r = doc.report || {};
  const sc = r.scores || {};
  return (
    <div className={`rounded-xl border bg-[#121721] p-6 ${isCurrent ? "border-[#00F076]/50" : "border-[#1E2638]"}`} data-testid={`report-card-${doc.id}`}>
      <div className="flex justify-between items-start gap-3">
        <div>
          <div className="text-[10px] font-mono-tech tracking-[0.25em] text-emerald-400 uppercase">{doc.week_key} {isCurrent && `· ${t.current}`}</div>
          <div className="font-display font-black text-2xl mt-1 leading-tight" data-testid="report-headline">{r.headline}</div>
        </div>
        {onDelete && (
          <Button variant="ghost" size="sm" onClick={() => onDelete(doc.id)} className="text-slate-400 hover:text-red-400 shrink-0" data-testid={`report-delete-${doc.id}`}><Trash2 className="w-4 h-4" /></Button>
        )}
      </div>
      {Object.keys(sc).length > 0 && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {SCORE_META.filter((m) => sc[m.k] != null).map((m) => (
            <div key={m.k} className="rounded-lg bg-[#090C10] border border-[#1E2638] p-3" data-testid={`report-score-${m.k}`}>
              <div className="text-[10px] font-mono-tech uppercase text-slate-400">{t[m.k]}</div>
              <div className="font-display font-black text-3xl" style={{ color: m.color }}>{sc[m.k]}</div>
              <div className="h-1 rounded bg-[#1E2638] mt-1"><div className="h-1 rounded" style={{ width: `${sc[m.k]}%`, background: m.color }} /></div>
            </div>
          ))}
        </div>
      )}
      {r.summary && <p className="mt-4 text-sm text-slate-300 leading-relaxed">{r.summary}</p>}
      <div className="mt-5 grid md:grid-cols-2 gap-5">
        <Section icon={TrendingUp} title={t.patterns} items={r.patterns} color="#60a5fa" testid="report-patterns" />
        <Section icon={AlertTriangle} title={t.risks} items={r.risks} color="#FF2E54" testid="report-risks" />
        <Section icon={Trophy} title={t.wins} items={r.wins} color="#00F076" testid="report-wins" />
        <Section icon={ListChecks} title={t.recos} items={r.recommendations} color="#FFB800" testid="report-recos" />
      </div>
      {r.next_week_focus?.length > 0 && (
        <div className="mt-5 rounded-lg border border-[#00F076]/30 bg-[#00F076]/5 p-4" data-testid="report-focus">
          <div className="text-xs font-mono-tech uppercase tracking-wider text-[#00F076] flex items-center gap-1 mb-2"><Target className="w-3.5 h-3.5" />{t.focus}</div>
          <div className="flex flex-wrap gap-2">{r.next_week_focus.map((f, i) => <span key={i} className="px-3 py-1 rounded-full bg-[#00F076] text-black text-xs font-bold">{f}</span>)}</div>
        </div>
      )}
      {doc.counts && (
        <div className="mt-3 text-[10px] font-mono-tech uppercase text-slate-500">
          {t.based}: {doc.counts.daily} daily · {doc.counts.medical} medical · {doc.counts.coach} coach · {doc.counts.videos} video
        </div>
      )}
    </div>
  );
};

export default function Report() {
  const { lang } = useLang();
  const t = L[lang] || L.en;
  const { can } = useAuth();
  const canReport = can("report");
  const [data, setData] = useState({ items: [], current_week_key: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/reports/weekly").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post("/reports/weekly/generate", { lang, auto: false });
      toast.success("Report ready");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => { await api.delete(`/reports/weekly/${id}`); load(); };

  const current = data.items.find((i) => i.week_key === data.current_week_key);
  const past = data.items.filter((i) => i.week_key !== data.current_week_key);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="report-page">
      <div className="text-[10px] font-mono-tech tracking-[0.3em] text-emerald-400">GPT 5.6 TERRA · PERFORMANCE DIRECTOR</div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display font-black uppercase text-4xl sm:text-5xl mt-2" data-testid="report-title">{t.title}</h1>
          <p className="text-slate-400 mt-2 max-w-2xl">{t.subtitle}</p>
        </div>
        <Button onClick={generate} disabled={busy || !canReport} data-testid="report-generate-btn" className="bg-[#00F076] hover:bg-[#00C963] text-black font-bold uppercase tracking-wider">
          <Sparkles className="w-4 h-4 mr-2" />{busy ? t.generating : current ? t.regen : t.generate}
        </Button>
      </div>

      <div className="mt-8 space-y-6">
        {!canReport && <UpgradeLock feature="report" requiredPlan="Pro" />}
        {busy && !current && (
          <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-10 text-center text-slate-400 animate-pulse" data-testid="report-loading"><Radar className="w-8 h-8 mx-auto mb-2 text-[#00F076]" />{t.generating}</div>
        )}
        {current ? <ReportCard doc={current} t={t} onDelete={del} isCurrent /> : !busy && (
          <div className="rounded-xl border border-dashed border-[#1E2638] p-10 text-center text-slate-500 text-sm" data-testid="report-empty">{t.empty}</div>
        )}
        {past.length > 0 && (
          <>
            <div className="font-display font-bold uppercase text-lg pt-4">{t.history}</div>
            {past.map((d) => <ReportCard key={d.id} doc={d} t={t} onDelete={del} />)}
          </>
        )}
      </div>
    </div>
  );
}
